// The brand's public configuration (spec/03 §12), read before anyone signs in: the sign-up screen
// needs the terms and privacy policy in force (`GET /v1/meta`, `brand.legal`, F-CMP-06). Read
// defensively, as every brand read is (`NotForYourAccount.tsx`'s `useVenueConsoleUrl`): `legal` is
// optional at every layer of the brand configuration (spec/03 §12.4) — a deployment that has named
// neither document yet offers nothing to consent to, so sign-up says so rather than inventing a
// version to send.

import type { ApiClient, SocialProvider, SocialSignInProvider } from '@clavian/api-client';

/** A legal document as `GET /v1/meta` names it: the version a sign-up must show, and where to read it. */
export interface BrandLegalDocument {
  readonly version: string;
  readonly url: string;
}

/** The terms and the privacy policy in force. `undefined` while the brand names neither (spec/03 §12.4). */
export interface BrandLegal {
  readonly terms: BrandLegalDocument;
  readonly privacy: BrandLegalDocument;
}

function document(value: unknown): BrandLegalDocument | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const version = (value as Record<string, unknown>)['version'];
  const url = (value as Record<string, unknown>)['url'];
  return typeof version === 'string' && typeof url === 'string' ? { version, url } : undefined;
}

export async function fetchBrandLegal(api: ApiClient): Promise<BrandLegal | undefined> {
  const body: unknown = await api.getMeta();
  const legal: unknown = (body as { brand?: { legal?: unknown } } | null)?.brand?.legal;
  if (typeof legal !== 'object' || legal === null) return undefined;
  const terms = document((legal as Record<string, unknown>)['terms']);
  const privacy = document((legal as Record<string, unknown>)['privacy']);
  return terms === undefined || privacy === undefined ? undefined : { terms, privacy };
}

/** The ways in that are on here (`GET /v1/meta`'s `signIn`): what a sign-in, sign-up or invitation screen may offer. */
export interface SignInMethods {
  /**
   * The social providers this deployment has switched on (F-IAM-03 … F-IAM-05): Google and Discord, and Steam since
   * 2026-10-08. The console signs in with Google alone; a staff member links any of them from their account.
   */
  readonly social: readonly SocialProvider[];
  /**
   * Whether a phone and a code sign anyone in, up or through an invitation (`auth.phoneSignIn`, the Platform's
   * switch; off since 2026-10-08). Off: no phone option anywhere, and invitations go by email.
   */
  readonly phone: boolean;
}

/**
 * `GET /v1/meta`'s `signIn`, read defensively, as `legal` is: an answer without the social providers offers
 * none, and an answer that does not say phone sign-in is on — a server from before the switch included — is
 * read as off, so a screen offers only a way in that works.
 */
export async function fetchSignInMethods(api: ApiClient): Promise<SignInMethods> {
  const body: unknown = await api.getMeta();
  const signIn = (body as { signIn?: { social?: unknown; phone?: unknown } } | null)?.signIn;
  const social: unknown = signIn?.social;
  return {
    social: Array.isArray(social)
      ? social.filter((provider): provider is SocialProvider => provider === 'google' || provider === 'discord' || provider === 'steam')
      : [],
    phone: signIn?.phone === true,
  };
}

/** The OAuth sign-in providers this deployment has switched on (`signIn.social`, F-IAM-03): Steam has its own endpoints. */
export async function fetchSocialProviders(api: ApiClient): Promise<readonly SocialSignInProvider[]> {
  return (await fetchSignInMethods(api)).social.filter((provider): provider is SocialSignInProvider => provider !== 'steam');
}
